
import React, { useState } from 'react';

// Type definitions
interface SubnetInput {
  id: number;
  name: string;
  hosts: string;
}

interface SubnetResult {
  name: string;
  network: string;
  networkAddress: string;
  subnetMask: string;
  broadcastAddress: string;
  firstUsable: string;
  lastUsable: string;
  totalHosts: number;
  usableHosts: number;
  awsUsableHosts: number;
  requested: number;
  prefixLength: number;
}

interface VLSMResults {
  subnets: SubnetResult[];
  totalUsed: number;
  totalAvailable: number;
  utilization: string;
}

interface Requirement {
  name: string;
  hosts: number;
}

const VLSMCalculator: React.FC = () => {
  const [baseNetwork, setBaseNetwork] = useState<string>('10.0.0.0/16');
  const [subnets, setSubnets] = useState<SubnetInput[]>([{ id: Date.now(), name: '', hosts: '' }]);
  const [results, setResults] = useState<VLSMResults | null>(null);
  const [error, setError] = useState<string>('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');

  // Theme CSS variables
  const themeStyles = {
    light: {
      '--bg-gradient': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      '--container-bg': 'white',
      '--header-gradient': 'linear-gradient(135deg, #232526 0%, #414345 100%)',
      '--header-text': 'white',
      '--text-primary': '#2c3e50',
      '--text-secondary': '#5d6d7e',
      '--text-muted': '#7f8c8d',
      '--section-bg': '#f8f9fa',
      '--card-bg': 'white',
      '--border-color': '#e9ecef',
      '--border-hover': '#667eea',
      '--input-border': '#ddd',
      '--input-focus': '#667eea',
      '--shadow': 'rgba(0,0,0,0.1)',
      '--shadow-hover': 'rgba(0,0,0,0.2)'
    },
    dark: {
      '--bg-gradient': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      '--container-bg': '#1e1e2e',
      '--header-gradient': 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
      '--header-text': '#e2e8f0',
      '--text-primary': '#e2e8f0',
      '--text-secondary': '#cbd5e1',
      '--text-muted': '#94a3b8',
      '--section-bg': '#262640',
      '--card-bg': '#2d2d44',
      '--border-color': '#374151',
      '--border-hover': '#667eea',
      '--input-border': '#374151',
      '--input-focus': '#667eea',
      '--shadow': 'rgba(0,0,0,0.3)',
      '--shadow-hover': 'rgba(0,0,0,0.5)'
    }
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'var(--bg-gradient)',
      minHeight: '100vh',
      padding: '20px',
      transition: 'all 0.3s ease',
      ...themeStyles[theme]
    },
    mainContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      background: 'var(--container-bg)',
      borderRadius: '15px',
      boxShadow: '0 20px 40px var(--shadow)',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    },
    header: {
      background: 'var(--header-gradient)',
      color: 'var(--header-text)',
      padding: '30px',
      textAlign: 'center' as React.CSSProperties['textAlign'],
      position: 'relative' as React.CSSProperties['position']
    },
    themeToggle: {
      position: 'absolute' as React.CSSProperties['position'],
      top: '30px',
      right: '30px',
      background: 'rgba(255,255,255,0.1)',
      border: '2px solid rgba(255,255,255,0.2)',
      borderRadius: '25px',
      padding: '8px 16px',
      color: 'var(--header-text)',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    content: {
      padding: '30px'
    },
    section: {
      background: 'var(--section-bg)',
      padding: '25px',
      borderRadius: '10px',
      marginBottom: '30px',
      transition: 'all 0.3s ease'
    },
    card: {
      background: 'var(--card-bg)',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '15px',
      transition: 'all 0.3s ease'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: '600',
      color: 'var(--text-primary)'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '2px solid var(--input-border)',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'all 0.3s ease',
      background: 'var(--card-bg)',
      color: 'var(--text-primary)'
    },
    button: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '12px 25px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase' as React.CSSProperties['textTransform'],
      letterSpacing: '0.5px'
    },
    buttonDanger: {
      background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)'
    },
    error: {
      background: '#f8d7da',
      color: '#721c24',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
      border: '1px solid #f5c6cb'
    }
  };

  const addSubnet = () => {
    setSubnets([...subnets, { id: Date.now(), name: '', hosts: '' }]);
  };

  const removeSubnet = (id: number) => {
    if (subnets.length > 1) {
      setSubnets(subnets.filter(subnet => subnet.id !== id));
    }
  };

  const updateSubnet = (id: number, field: keyof SubnetInput, value: string) => {
    setSubnets(subnets.map(subnet => 
      subnet.id === id ? { ...subnet, [field]: value } : subnet
    ));
  };

  const clearAll = () => {
    setSubnets([{ id: Date.now(), name: '', hosts: '' }]);
    setResults(null);
    setError('');
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // VLSM Calculation Functions
  const parseIPv4 = (ip: string): number => {
    const parts = ip.split('.').map(Number);
    return (parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3];
  };

  const formatIPv4 = (num: number): string => {
    return [
      (num >>> 24) & 0xFF,
      (num >>> 16) & 0xFF,
      (num >>> 8) & 0xFF,
      num & 0xFF
    ].join('.');
  };

  const calculateSubnetMask = (prefixLength: number): number => {
    return (~((1 << (32 - prefixLength)) - 1)) >>> 0;
  };

  const getNextPowerOf2 = (n: number): number => {
    return Math.pow(2, Math.ceil(Math.log2(n + 5)));
  };

  const performVLSM = (baseIP: string, basePrefixLength: number, requirements: Requirement[]): VLSMResults => {
    const baseAddress = parseIPv4(baseIP);
    const baseMask = calculateSubnetMask(basePrefixLength);
    const networkAddress = baseAddress & baseMask;
    const availableAddresses = Math.pow(2, 32 - basePrefixLength);
    
    const sortedRequirements = requirements
      .map((req: Requirement) => ({
        ...req,
        requiredSize: getNextPowerOf2(req.hosts)
      }))
      .sort((a: any, b: any) => b.requiredSize - a.requiredSize);

    let currentAddress = networkAddress;
    const allocatedSubnets: SubnetResult[] = [];
    let totalUsed = 0;

    for (let req of sortedRequirements) {
      const subnetSize = req.requiredSize;
      const subnetPrefixLength = 32 - Math.log2(subnetSize);
      
      if (totalUsed + subnetSize > availableAddresses) {
        throw new Error(`Cannot fit subnet "${req.name}" (needs ${subnetSize} addresses). Not enough space in base network.`);
      }
      
      const mask = calculateSubnetMask(subnetPrefixLength);
      currentAddress = (currentAddress + subnetSize - 1) & mask;
      
      if (currentAddress + subnetSize - 1 > networkAddress + availableAddresses - 1) {
        throw new Error(`Cannot fit subnet "${req.name}". Address space exhausted.`);
      }

      const subnetMask = calculateSubnetMask(subnetPrefixLength);
      const broadcastAddress = currentAddress | (~subnetMask >>> 0);
      const firstUsable = currentAddress + 1;
      const lastUsable = broadcastAddress - 1;
      const totalHosts = subnetSize - 2;
      const usableHosts = totalHosts - 3;

      allocatedSubnets.push({
        name: req.name,
        network: `${formatIPv4(currentAddress)}/${subnetPrefixLength}`,
        networkAddress: formatIPv4(currentAddress),
        subnetMask: formatIPv4(subnetMask),
        broadcastAddress: formatIPv4(broadcastAddress),
        firstUsable: formatIPv4(firstUsable),
        lastUsable: formatIPv4(lastUsable),
        totalHosts,
        usableHosts,
        awsUsableHosts: Math.max(0, usableHosts),
        requested: req.hosts,
        prefixLength: subnetPrefixLength
      });

      currentAddress += subnetSize;
      totalUsed += subnetSize;
    }

    return {
      subnets: allocatedSubnets,
      totalUsed,
      totalAvailable: availableAddresses,
      utilization: ((totalUsed / availableAddresses) * 100).toFixed(1)
    };
  };

  const calculateVLSM = () => {
    setError('');
    
    if (!baseNetwork.match(/^\d+\.\d+\.\d+\.\d+\/\d+$/)) {
      setError('Please enter a valid CIDR notation (e.g., 10.0.0.0/16)');
      return;
    }

    const [baseIP, basePrefixLength] = baseNetwork.split('/');
    const basePrefixNum = parseInt(basePrefixLength);
    
    if (basePrefixNum < 8 || basePrefixNum > 28) {
      setError('Prefix length must be between /8 and /28');
      return;
    }

    const requirements: Requirement[] = [];
    
    for (let subnet of subnets) {
      const hostCount = parseInt(subnet.hosts);
      if (!subnet.name.trim() || !subnet.hosts || hostCount < 1) {
        setError('Please fill in all subnet names and host requirements (minimum 1 host)');
        return;
      }
      requirements.push({ name: subnet.name.trim(), hosts: hostCount });
    }

    try {
      const result = performVLSM(baseIP, basePrefixNum, requirements);
      setResults(result);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  const copyResults = async () => {
    if (!results) return;
    
    let text = "AWS VPC VLSM Subnet Plan\n";
    text += "========================================\n\n";
    
    results.subnets.forEach(subnet => {
      text += `${subnet.name}\n`;
      text += "-".repeat(subnet.name.length) + "\n";
      text += `Network: ${subnet.network}\n`;
      text += `Subnet Mask: ${subnet.subnetMask}\n`;
      text += `Network Address: ${subnet.networkAddress}\n`;
      text += `Broadcast Address: ${subnet.broadcastAddress}\n`;
      text += `First Usable IP: ${subnet.firstUsable}\n`;
      text += `Last Usable IP: ${subnet.lastUsable}\n`;
      text += `Total Host IPs: ${subnet.totalHosts.toLocaleString()}\n`;
      text += `AWS Usable IPs: ${subnet.awsUsableHosts.toLocaleString()}\n`;
      text += `Requested: ${subnet.requested.toLocaleString()}\n\n`;
    });
    
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.mainContainer}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={{ fontSize: '2.5em', marginBottom: '10px' }}>AWS VPC VLSM Calculator</h1>
          <p style={{ opacity: 0.8, fontSize: '1.1em' }}>
            Efficiently plan your VPC subnets with Variable Length Subnet Masking
          </p>
          <button 
            style={styles.themeToggle}
            onClick={toggleTheme}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.transform = 'translateY(0)';
            }}
          >
            <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
            <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
        </div>

        <div style={styles.content}>
          {/* Educational Notes Section */}
          <div style={{...styles.section, border: '2px solid #3498db'}}>
            <h2 style={{ color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '10px' }}>📚</span>
              IP Addresses & CIDR Fundamentals
            </h2>
            
            {/* IP Address Basics */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>🌐 What is an IP Address?</h3>
              <div style={styles.card}>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '15px' }}>
                  An IPv4 address is a <strong>32-bit number</strong> written as four decimal numbers (0-255) separated by dots. 
                  Each decimal number represents 8 bits (1 byte), called an <strong>octet</strong>.
                </p>
                <div style={{
                  background: 'var(--section-bg)',
                  padding: '15px',
                  borderRadius: '6px',
                  fontFamily: "'Courier New', monospace",
                  textAlign: 'center',
                  marginBottom: '15px'
                }}>
                  <div style={{ marginBottom: '10px', fontSize: '1.2em', color: 'var(--text-primary)' }}>
                    <strong>192.168.1.100</strong>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>
                    ↓ converts to binary ↓
                  </div>
                  <div style={{ marginTop: '10px', color: '#e74c3c' }}>
                    11000000.10101000.00000001.01100100
                  </div>
                </div>
              </div>
            </div>

            {/* Private IP Ranges */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>🏠 Private IP Address Ranges (RFC 1918)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                {[
                  {
                    title: 'Class A Private',
                    cidr: '10.0.0.0/8',
                    color: '#e74c3c',
                    details: ['Range: 10.0.0.0 - 10.255.255.255', '16,777,216 addresses', 'Perfect for large organizations', 'AWS VPC default choice']
                  },
                  {
                    title: 'Class B Private',
                    cidr: '172.16.0.0/12',
                    color: '#f39c12',
                    details: ['Range: 172.16.0.0 - 172.31.255.255', '1,048,576 addresses', 'Medium-sized networks', 'Docker default range']
                  },
                  {
                    title: 'Class C Private',
                    cidr: '192.168.0.0/16',
                    color: '#27ae60',
                    details: ['Range: 192.168.0.0 - 192.168.255.255', '65,536 addresses', 'Home networks, small offices', 'Most home routers use this']
                  }
                ].map((range, idx) => (
                  <div key={idx} style={{
                    ...styles.card,
                    borderLeft: `4px solid ${range.color}`
                  }}>
                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>{range.title}</h4>
                    <div style={{
                      fontFamily: "'Courier New', monospace",
                      color: range.color,
                      fontSize: '1.1em',
                      marginBottom: '10px'
                    }}>
                      <strong>{range.cidr}</strong>
                    </div>
                    <ul style={{
                      color: 'var(--text-secondary)',
                      lineHeight: 1.6,
                      marginLeft: '20px',
                      fontSize: '0.9em'
                    }}>
                      {range.details.map((detail, i) => (
                        <li key={i}>{detail.includes('AWS') ? <strong>{detail}</strong> : detail}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Common CIDR Ranges */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>📊 Common CIDR Ranges & Sizes</h3>
              <div style={styles.card}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {[
                    { prefix: '/8', ips: '16,777,216 IPs', use: 'AWS Region' },
                    { prefix: '/16', ips: '65,536 IPs', use: 'Large VPC' },
                    { prefix: '/20', ips: '4,096 IPs', use: 'Medium VPC' },
                    { prefix: '/24', ips: '256 IPs', use: 'Standard Subnet' },
                    { prefix: '/26', ips: '64 IPs', use: 'Small Subnet' },
                    { prefix: '/28', ips: '16 IPs', use: 'Micro Subnet' }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      textAlign: 'center',
                      padding: '15px',
                      background: 'var(--section-bg)',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontFamily: "'Courier New', monospace",
                        fontSize: '1.2em',
                        color: 'var(--text-primary)',
                        marginBottom: '5px'
                      }}>
                        <strong>{item.prefix}</strong>
                      </div>
                      <div style={{ color: '#e74c3c', fontWeight: '600', marginBottom: '5px' }}>
                        {item.ips}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>
                        {item.use}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: '15px',
                  padding: '15px',
                  background: 'var(--section-bg)',
                  borderRadius: '6px',
                  borderLeft: '4px solid #3498db'
                }}>
                  <strong style={{ color: 'var(--text-primary)' }}>💡 Quick Formula:</strong>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: '10px' }}>
                    Number of IPs = 2^(32 - prefix length)
                  </span>
                  <br />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Example: /24 = 2^(32-24) = 2^8 = 256 IP addresses
                  </small>
                </div>
              </div>
            </div>

            {/* AWS Best Practices */}
            <div style={{
              background: '#fff3cd',
              padding: '20px',
              borderRadius: '8px',
              borderLeft: '4px solid #f39c12'
            }}>
              <h4 style={{ color: '#856404', marginBottom: '15px' }}>☁️ AWS VPC Best Practices</h4>
              <div style={{ color: '#856404', lineHeight: 1.6 }}>
                <ul style={{ marginLeft: '20px' }}>
                  <li><strong>Use /16 for VPC:</strong> Gives you 65K IPs with plenty of room to grow</li>
                  <li><strong>Use /24 for subnets:</strong> Standard size with 256 IPs (251 usable in AWS)</li>
                  <li><strong>Plan for multiple AZs:</strong> Create subnets in different Availability Zones</li>
                  <li><strong>Reserve space:</strong> Don't use all your CIDR space immediately</li>
                  <li><strong>Use 10.x.x.x:</strong> Most flexible private range for large deployments</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div style={styles.section}>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Base Network (CIDR)</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g., 10.0.0.0/16"
                  value={baseNetwork}
                  onChange={(e) => setBaseNetwork(e.target.value)}
                />
              </div>
              <button style={styles.button} onClick={addSubnet}>
                Add Subnet
              </button>
            </div>

            {/* Subnet Inputs */}
            {subnets.map((subnet) => (
              <div key={subnet.id} style={{
                background: 'var(--card-bg)',
                border: '2px solid var(--border-color)',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '15px'
              }}>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Subnet Name</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="e.g., Public-Subnet-A"
                      value={subnet.name}
                      onChange={(e) => updateSubnet(subnet.id, 'name', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Required Hosts</label>
                    <input
                      type="number"
                      style={styles.input}
                      placeholder="e.g., 100"
                      min="1"
                      value={subnet.hosts}
                      onChange={(e) => updateSubnet(subnet.id, 'hosts', e.target.value)}
                    />
                  </div>
                  <button 
                    style={{...styles.button, ...styles.buttonDanger}}
                    onClick={() => removeSubnet(subnet.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px' }}>
              <button style={styles.button} onClick={calculateVLSM}>
                Calculate VLSM
              </button>
              <button style={{...styles.button, padding: '8px 15px', fontSize: '14px'}} onClick={clearAll}>
                Clear All
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {/* Results Section */}
          {results && (
            <div style={{...styles.section, overflow: 'hidden'}}>
              <div style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '-25px -25px 20px -25px'
              }}>
                <h2>Subnet Allocation Results</h2>
                <button 
                  style={{...styles.button, padding: '8px 15px', fontSize: '14px'}}
                  onClick={copyResults}
                >
                  Copy Results
                </button>
              </div>

              {/* Summary */}
              <div style={styles.card}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  {[
                    { 
                      number: results.subnets.length, 
                      label: 'Subnets Created',
                      color: '#667eea'
                    },
                    { 
                      number: results.totalUsed.toLocaleString(), 
                      label: 'IPs Allocated',
                      color: '#667eea'
                    },
                    { 
                      number: `${results.utilization}%`, 
                      label: 'Network Utilization',
                      color: '#667eea'
                    },
                    { 
                      number: (results.totalAvailable - results.totalUsed).toLocaleString(), 
                      label: 'Network Space Remaining',
                      subtitle: '(Available for new subnets)',
                      color: '#27ae60'
                    },
                    { 
                      number: results.subnets.reduce((sum, subnet) => sum + (subnet.awsUsableHosts - subnet.requested), 0).toLocaleString(), 
                      label: 'Total Wasted IPs',
                      subtitle: '(Unused in allocated subnets)',
                      color: '#e74c3c'
                    }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      textAlign: 'center',
                      padding: '15px',
                      background: 'var(--section-bg)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '2em', fontWeight: '700', color: item.color }}>
                        {item.number}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontWeight: '600', marginTop: '5px' }}>
                        {item.label}
                      </div>
                      {item.subtitle && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.8em', marginTop: '2px' }}>
                          {item.subtitle}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Explanation */}
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#e3f2fd',
                  borderRadius: '8px',
                  borderLeft: '4px solid #2196f3'
                }}>
                  <h4 style={{ color: '#1565c0', marginBottom: '10px', fontSize: '16px' }}>📊 Understanding the Numbers</h4>
                  <div style={{ color: '#1976d2', fontSize: '14px', lineHeight: 1.6 }}>
                    <p style={{ marginBottom: '10px' }}>
                      <strong>Network Space Remaining:</strong> Unused address space in your base network ({baseNetwork}) that could accommodate additional subnets.
                    </p>
                    <p style={{ marginBottom: '0' }}>
                      <strong>Total Wasted IPs:</strong> IP addresses within allocated subnets that are unused due to VLSM's power-of-2 allocation (e.g., requesting 100 hosts gets you 128, wasting 28).
                    </p>
                  </div>
                </div>
              </div>

              {/* Subnet Details Table */}
              <div style={{
                ...styles.card,
                padding: '0',
                overflow: 'auto'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse' as React.CSSProperties['borderCollapse'],
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                      borderBottom: '3px solid #4f46e5'
                    }}>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'left' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>📝 Name</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'center' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>🎯 Hosts Needed</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'center' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>✅ Hosts Available</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'center' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>❌ Unused Hosts</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'left' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>🌐 Network Address</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'center' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>🔢 Slash</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'left' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>🎭 Mask</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'left' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        borderRight: '2px solid #6b7280',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>📡 Usable Range</th>
                      <th style={{
                        padding: '18px 12px',
                        textAlign: 'left' as React.CSSProperties['textAlign'],
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '14px',
                        textTransform: 'uppercase' as React.CSSProperties['textTransform'],
                        letterSpacing: '0.5px'
                      }}>📢 Broadcast</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.subnets.map((subnet, idx) => {
                      // Alternate row color schemes - choose one by uncommenting
                      
                      // Option 1: Subtle alternating grays (current enhanced)
                      const isEvenRow = idx % 2 === 0;
                      const rowBg = isEvenRow ? '#374151' : '#4b5563';
                      
                      // Option 2: Blue/Purple alternating (uncomment to use)
                      // const isEvenRow = idx % 2 === 0;
                      // const rowBg = isEvenRow ? '#1e40af' : '#7c3aed';
                      
                      // Option 3: Dark/Darker alternating (uncomment to use)
                      // const isEvenRow = idx % 2 === 0;
                      // const rowBg = isEvenRow ? '#1f2937' : '#111827';
                      
                      // Option 4: Navy/Teal alternating (uncomment to use)
                      // const isEvenRow = idx % 2 === 0;
                      // const rowBg = isEvenRow ? '#1e3a8a' : '#115e59';
                      
                      return (
                        <tr key={idx} style={{
                          borderBottom: '2px solid #6b7280',
                          backgroundColor: rowBg,
                          transition: 'background-color 0.2s ease'
                        }}>
                          <td style={{
                            padding: '15px 12px',
                            color: '#ffffff',
                            fontWeight: '700',
                            fontSize: '14px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#3b82f6'
                          }}>{subnet.name}</td>
                          <td style={{
                            padding: '15px 12px',
                            textAlign: 'center' as React.CSSProperties['textAlign'],
                            color: '#1f2937',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '14px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#fde047'
                          }}>{subnet.requested.toLocaleString()}</td>
                          <td style={{
                            padding: '15px 12px',
                            textAlign: 'center' as React.CSSProperties['textAlign'],
                            color: '#1f2937',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '14px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#4ade80'
                          }}>{subnet.awsUsableHosts.toLocaleString()}</td>
                          <td style={{
                            padding: '15px 12px',
                            textAlign: 'center' as React.CSSProperties['textAlign'],
                            color: subnet.awsUsableHosts - subnet.requested > 0 ? '#1f2937' : '#e5e7eb',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '14px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: subnet.awsUsableHosts - subnet.requested > 0 ? '#f87171' : '#6b7280'
                          }}>{(subnet.awsUsableHosts - subnet.requested).toLocaleString()}</td>
                          <td style={{
                            padding: '15px 12px',
                            color: '#1f2937',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '14px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#93c5fd'
                          }}>{subnet.networkAddress}</td>
                          <td style={{
                            padding: '15px 12px',
                            textAlign: 'center' as React.CSSProperties['textAlign'],
                            color: '#1f2937',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '800',
                            fontSize: '15px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#fb923c'
                          }}>/{subnet.prefixLength}</td>
                          <td style={{
                            padding: '15px 12px',
                            color: '#1f2937',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '14px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#d8b4fe'
                          }}>{subnet.subnetMask}</td>
                          <td style={{
                            padding: '15px 12px',
                            color: '#1f2937',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '13px',
                            borderRight: '2px solid #6b7280',
                            backgroundColor: '#67e8f9'
                          }}>{subnet.firstUsable} - {subnet.lastUsable}</td>
                          <td style={{
                            padding: '15px 12px',
                            color: '#ffffff',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700',
                            fontSize: '14px',
                            backgroundColor: '#f472b6'
                          }}>{subnet.broadcastAddress}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Possible Additional Subnets */}
              <div style={{
                background: 'var(--section-bg)',
                padding: '20px',
                borderRadius: '10px',
                marginTop: '20px',
                border: '2px solid #10b981'
              }}>
                <h3 style={{ 
                  color: 'var(--text-primary)', 
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '18px'
                }}>
                  <span style={{ marginRight: '10px' }}>🚀</span>
                  Possible Additional Subnets
                  <span style={{ 
                    marginLeft: '10px', 
                    fontSize: '14px', 
                    color: '#10b981',
                    fontWeight: 'normal'
                  }}>
                    ({(results.totalAvailable - results.totalUsed).toLocaleString()} IPs remaining)
                  </span>
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  {(() => {
                    // Calculate the next available IP address
                    const [baseIP, basePrefixLength] = baseNetwork.split('/');
                    const baseAddress = parseIPv4(baseIP);
                    const baseMask = calculateSubnetMask(parseInt(basePrefixLength));
                    const networkAddress = baseAddress & baseMask;
                    
                    // Find the next available address after all allocated subnets
                    let nextAvailableAddress = networkAddress;
                    
                    // Sort existing subnets by network address to find the next available spot
                    const sortedSubnets = [...results.subnets].sort((a, b) => 
                      parseIPv4(a.networkAddress) - parseIPv4(b.networkAddress)
                    );
                    
                    for (let subnet of sortedSubnets) {
                      const subnetAddr = parseIPv4(subnet.networkAddress);
                      const subnetSize = Math.pow(2, 32 - subnet.prefixLength);
                      if (subnetAddr >= nextAvailableAddress) {
                        nextAvailableAddress = subnetAddr + subnetSize;
                      }
                    }
                    
                    // Calculate possible subnet sizes with actual network addresses
                    const subnetSizes = [
                      { size: 16384, prefix: '/18', hosts: 16381, awsHosts: 16379, use: 'Very Large Subnet' },
                      { size: 8192, prefix: '/19', hosts: 8189, awsHosts: 8187, use: 'Large Subnet' },
                      { size: 4096, prefix: '/20', hosts: 4093, awsHosts: 4091, use: 'Medium-Large Subnet' },
                      { size: 2048, prefix: '/21', hosts: 2045, awsHosts: 2043, use: 'Medium Subnet' },
                      { size: 1024, prefix: '/22', hosts: 1021, awsHosts: 1019, use: 'Small-Medium Subnet' },
                      { size: 512, prefix: '/23', hosts: 509, awsHosts: 507, use: 'Small Subnet' },
                      { size: 256, prefix: '/24', hosts: 253, awsHosts: 251, use: 'Standard Subnet' },
                      { size: 128, prefix: '/25', hosts: 125, awsHosts: 123, use: 'Small Subnet' },
                      { size: 64, prefix: '/26', hosts: 61, awsHosts: 59, use: 'Micro Subnet' },
                      { size: 32, prefix: '/27', hosts: 29, awsHosts: 27, use: 'Tiny Subnet' },
                      { size: 16, prefix: '/28', hosts: 13, awsHosts: 11, use: 'Minimal Subnet' }
                    ];
                    
                    const possibleSubnets = [];
                    let currentAddress = nextAvailableAddress;
                    const networkEnd = networkAddress + results.totalAvailable;
                    
                    for (let subnet of subnetSizes) {
                      const prefixLength = parseInt(subnet.prefix.substring(1));
                      const mask = calculateSubnetMask(prefixLength);
                      
                      // Align to subnet boundary
                      const alignedAddress = (currentAddress + subnet.size - 1) & mask;
                      
                      if (alignedAddress + subnet.size <= networkEnd) {
                        const count = Math.floor((networkEnd - alignedAddress) / subnet.size);
                        if (count > 0) {
                          const networks = [];
                          let addr = alignedAddress;
                          
                          // Generate up to 5 example networks for display
                          for (let i = 0; i < Math.min(count, 5); i++) {
                            networks.push(`${formatIPv4(addr)}${subnet.prefix}`);
                            addr += subnet.size;
                          }
                          
                          possibleSubnets.push({
                            ...subnet,
                            count,
                            totalIPsUsed: count * subnet.size,
                            networks,
                            startAddress: alignedAddress
                          });
                        }
                      }
                    }
                    
                    return possibleSubnets.slice(0, 6).map((subnet, idx) => (
                      <div key={idx} style={{
                        background: 'var(--card-bg)',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        transition: 'transform 0.2s ease'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '10px'
                        }}>
                          <span style={{
                            fontFamily: "'Courier New', monospace",
                            fontSize: '16px',
                            fontWeight: '800',
                            color: '#3b82f6'
                          }}>
                            {subnet.prefix}
                          </span>
                          <span style={{
                            background: '#10b981',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '700'
                          }}>
                            × {subnet.count}
                          </span>
                        </div>
                        
                        {/* Network Examples */}
                        <div style={{
                          background: 'var(--section-bg)',
                          padding: '10px',
                          borderRadius: '6px',
                          marginBottom: '10px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            color: 'var(--text-muted)',
                            marginBottom: '5px',
                            fontWeight: '600'
                          }}>
                            Available Networks:
                          </div>
                          {subnet.networks.map((network, netIdx) => (
                            <div key={netIdx} style={{
                              fontFamily: "'Courier New', monospace",
                              fontSize: '13px',
                              color: '#1f2937',
                              padding: '2px 6px',
                              background: '#f0f9ff',
                              borderRadius: '3px',
                              marginBottom: '3px',
                              border: '1px solid #bae6fd'
                            }}>
                              {network}
                            </div>
                          ))}
                          {subnet.count > 5 && (
                            <div style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              fontStyle: 'italic',
                              marginTop: '5px'
                            }}>
                              ... and {subnet.count - 5} more networks
                            </div>
                          )}
                        </div>
                        
                        <div style={{
                          color: 'var(--text-secondary)',
                          fontSize: '14px',
                          lineHeight: 1.4
                        }}>
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{subnet.awsHosts.toLocaleString()}</strong> AWS usable IPs each
                          </div>
                          <div style={{ marginBottom: '5px', color: 'var(--text-muted)' }}>
                            {subnet.use}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#059669',
                            fontWeight: '600'
                          }}>
                            Total: {(subnet.count * subnet.awsHosts).toLocaleString()} usable IPs
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                
                {/* Combination Examples */}
                <div style={{
                  background: '#eff6ff',
                  padding: '15px',
                  borderRadius: '8px',
                  borderLeft: '4px solid #3b82f6'
                }}>
                  <h4 style={{ color: '#1e40af', marginBottom: '10px', fontSize: '14px' }}>
                    💡 Smart Combinations
                  </h4>
                  <div style={{ color: '#1e40af', fontSize: '13px', lineHeight: 1.6 }}>
                    {(() => {
                      const remainingIPs = results.totalAvailable - results.totalUsed;
                      const combinations = [];
                      
                      // Generate some smart combinations
                      if (remainingIPs >= 1024) {
                        combinations.push('2× /22 (1,019 hosts each) + 4× /24 (251 hosts each) = 3,042 total usable hosts');
                      }
                      if (remainingIPs >= 512) {
                        combinations.push('1× /23 (507 hosts) + 2× /24 (251 hosts each) = 1,009 total usable hosts');
                      }
                      if (remainingIPs >= 256) {
                        combinations.push('Multiple /24 standard subnets (251 AWS usable hosts each)');
                      }
                      
                      return combinations.length > 0 ? (
                        <ul style={{ marginLeft: '15px' }}>
                          {combinations.map((combo, idx) => (
                            <li key={idx} style={{ marginBottom: '5px' }}>{combo}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>Consider smaller subnets (/26, /27, /28) for efficient use of remaining space.</p>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* AWS Note */}
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <strong style={{ color: '#856404', fontSize: '16px', fontWeight: '800' }}>⚠️ AWS Note:</strong> 
                <span style={{ color: '#721c24', fontWeight: '700', fontSize: '15px' }}>
                  AWS reserves the first 4 IP addresses and the last IP address in each subnet:
                </span>
                <ul style={{ marginTop: '10px', marginLeft: '20px', color: '#856404', fontWeight: '600' }}>
                  <li><strong>First IP:</strong> Network address</li>
                  <li><strong>Second IP:</strong> VPC router</li>
                  <li><strong>Third IP:</strong> DNS server</li>
                  <li><strong>Fourth IP:</strong> Future use</li>
                  <li><strong>Last IP:</strong> Broadcast address</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VLSMCalculator;
